import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';

interface EmbeddedMessagingUtilApi {
  launchChat?: (options?: { shouldStartNewConversation: boolean }) => Promise<void> | void;
  removeAllComponents?: () => void;
  sendMessage?: (message: string) => Promise<void> | void;
  sendTextMessage?: (message: string) => Promise<void> | void;
}

interface EmbeddedMessagingUserVerificationApi {
  clearSession: (shouldEndSession: boolean) => Promise<void>;
}

interface EmbeddedServiceBootstrap {
  removeEventHandlers?: () => void;
  utilAPI?: EmbeddedMessagingUtilApi;
  userVerificationAPI?: EmbeddedMessagingUserVerificationApi;
}

declare global {
  interface Window {
    embeddedservice_bootstrap?: EmbeddedServiceBootstrap;
  }
}

const READY_TIMEOUT_MS = 6000;
const READY_EVENT = 'onEmbeddedMessagingFirstBotMessageSent';
const CONVERSATION_STARTED_EVENT = 'onEmbeddedMessagingConversationStarted';
const CONVERSATION_CLOSED_EVENTS = [
  'onEmbeddedMessagingConversationClosed',
  'onEmbeddedMessagingWindowClosed',
] as const;

@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  private readonly http = inject(HttpClient);

  /** Whether a chatbot conversation is currently open in the browser tab. */
  private conversationActive = false;

  constructor() {
    const destroyRef = inject(DestroyRef);

    const onStart = (): void => {
      this.conversationActive = true;
    };
    const onClose = (): void => {
      this.conversationActive = false;
    };

    window.addEventListener(CONVERSATION_STARTED_EVENT, onStart);
    CONVERSATION_CLOSED_EVENTS.forEach(e => window.addEventListener(e, onClose));

    destroyRef.onDestroy(() => {
      window.removeEventListener(CONVERSATION_STARTED_EVENT, onStart);
      CONVERSATION_CLOSED_EVENTS.forEach(e => window.removeEventListener(e, onClose));
    });
  }

  getChatbotInitScript() {
    const options = {
      observe: 'body' as const,
      responseType: 'text' as const,
    };
    return this.http.get(`${environment.baseUrl}/portal/chatbot`, options);
  }

  async openWithPrompt(prompt: string): Promise<boolean> {
    const trimmedPrompt = prompt?.trim();
    const utilAPI = window.embeddedservice_bootstrap?.utilAPI;
    if (!trimmedPrompt || typeof utilAPI?.launchChat !== 'function') {
      return false;
    }

    const sendMessage = utilAPI.sendTextMessage ?? utilAPI.sendMessage;
    if (typeof sendMessage !== 'function') {
      return false;
    }

    try {
      // Fast path: conversation already open → skip launch & ready-wait, just send.
      if (this.conversationActive) {
        await sendMessage.call(utilAPI, trimmedPrompt);
        return true;
      }

      // First-time path: set up readiness listener BEFORE launching so we don't
      // miss the event. We wait for the first bot message because at that point
      // the conversation is fully established and the bot is ready to answer.
      // If the flow has no bot greeting the event won't fire, so we fall back
      // to sending after a timeout.
      const ready = new Promise<'event' | 'timeout'>(resolve => {
        const handler = (): void => resolve('event');
        window.addEventListener(READY_EVENT, handler, { once: true });
        setTimeout(() => {
          window.removeEventListener(READY_EVENT, handler);
          resolve('timeout');
        }, READY_TIMEOUT_MS);
      });

      await utilAPI.launchChat({ shouldStartNewConversation: true });
      const reason = await ready;
      if (reason === 'timeout') {
        console.warn('Chatbot ready event not received within timeout, sending anyway');
      }
      await sendMessage.call(utilAPI, trimmedPrompt);
      return true;
    } catch (error) {
      console.error('Chatbot openWithPrompt failed', error);
      return false;
    }
  }
}
